import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import UiIcon from 'components/Shared/Common/Icon';
import { getStatus, PRESENCE_STATUSES } from 'const/user';
import Avatar from 'components/Shared/User/Avatar';
import { getUrl } from 'utils/url';
import UserName from 'components/Shared/User/UserName';
import { StyledStatus } from 'components/Shared/User/Avatar/styles';
import { StyledAvatarButton, StyledStatusItem } from 'components/Shared/style';
import Dropdown from 'components/Shared/Common/Dropdown';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { colors } from 'common/themes/Colors';

import { updateAndPatchUser } from 'actions/session';
import { getCurrentUserSelector } from 'selectors/user';
import { withTour } from '@reactour/tour';

const UserNameStyle = { color: colors.fixedWhite };

const StatusItem = injectIntl(({
    onClick,
    href,
    labelId,
    subtitleId,
    className,
    isActive,
    intl
}) => (
    <StyledStatusItem 
        onClick={onClick} 
        href={href} 
        className={classNames('s-status-items', className)}
    >
        <div 
            className={classNames(
                'line',
                {
                    'active' : isActive
                }
            )}
        >
            <div className="text">
                {className && <StyledStatus className={classNames('s-status', className)} />}
                {intl.formatMessage({ id: labelId })}
            </div>
            {isActive && (<UiIcon iconName="Check" />)}
        </div>
        {subtitleId && (
            <span className="label">
                {intl.formatMessage({ id: subtitleId })}
            </span>
        )}
    </StyledStatusItem>
));

const AvatarButton = ({ avatarUri, status, lastname, firstname }) => (
    <StyledAvatarButton>
        <Avatar
            src={avatarUri}
            size="md"
            status={status}
            alt={`${firstname} ${lastname}`}
        />
        <UserName style={UserNameStyle} firstname={firstname} lastname={lastname} />
    </StyledAvatarButton>
)

const statusList = [PRESENCE_STATUSES.ONLINE, PRESENCE_STATUSES.AWAY, PRESENCE_STATUSES.BUSY, PRESENCE_STATUSES.OFFLINE].map(getStatus)

const StatusItemRenderer = ({ item, currentStatus, onChange }) => {
    // todo: rework saga for non bloking code
    // timeout for giving time to dropdown to close and the do action
    // without timeout, dropdown will be frozen during process
    const handleChange = useCallback(() => setTimeout(() => onChange(item.id), 250), [onChange, item.id]);

    return (
        <StatusItem
            subtitleId={item.subtitleId}
            color={item.color}
            className={item.className}
            labelId={item.labelId}
            onClick={handleChange}
            isActive={currentStatus === item.id} />
    );
}
const UserStatusSelector = withTour(({ onChange, status, firstname, lastname, avatarUri, intl, _user, setIsOpen, setCurrentStep }) => {

    const handleClick = useCallback(() => {
        setCurrentStep(0);
        setIsOpen(true);
    }, []);

    const handleFaqClick = useCallback(() => window.open(getUrl('faq', { lang: intl.locale }), 'faq'), []);
    return (
        <Dropdown items={[
            {
                label: intl.formatMessage({ id: 'menuLeft.navigation.profile' }),
                id: 'MyProfile',
                iconName: 'User',
                href: getUrl('profile', { _user })
            },
            {
                type: 'divider',
            },
            ...statusList.map(item => ({
                id: item.labelId,
                children: <StatusItemRenderer 
                    item={item} 
                    currentStatus={status} 
                    onChange={onChange} />
            })),
            {
                type: 'divider',
            },
            {
                label: intl.formatMessage({ id: 'menuLeft.navigation.settings' }),
                id: 'parameters',
                iconName: 'Settings',
                href: getUrl('settings'),
            },
            {
                label: intl.formatMessage({ id: 'menuLeft.navigation.help' }),
                id: 'faq',
                iconName: 'HelpCircle',
                submenuItems: [
                    {
                        id: 'faq-1',
                        iconName: 'LifeBuoy',
                        label: intl.formatMessage({ id: 'menuLeft.navigation.tour' }),
                        onClick: handleClick
                    },
                    {
                        id: 'faq-2',
                        iconName: 'Video',
                        label: intl.formatMessage({ id: 'menuLeft.navigation.faq' }),
                        onClick: handleFaqClick
                    },
                ],
            },
            {
                type: 'divider',
            },
            {
                label: intl.formatMessage({ id: 'menu.dropdown.logout' }),
                id: 'LogOut',
                iconName: 'LogOut',
                href: getUrl('logout'),
                danger: true
            }
        ]}>
            <div>
                <AvatarButton 
                    avatarUri={avatarUri}
                    status={status}
                    lastname={lastname}
                    firstname={firstname}
                />
            </div>
        </Dropdown>
    )
});

UserStatusSelector.propTypes = {
    // intl: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    status: PropTypes.oneOf(Object.values(PRESENCE_STATUSES)).isRequired,
    firstname: PropTypes.string.isRequired,
    lastname: PropTypes.string.isRequired,
    _user: PropTypes.string.isRequired,
    avatarUri: PropTypes.string
};

const mapStateToProps = state => {
    const user = getCurrentUserSelector(state);
    const { status, firstname, lastname, avatarUri, id } = user || {};

    return ({
        status, firstname, lastname, avatarUri, _user: id
    });
};

const mapDispatchToProps = dispatch => ({
    onChange: (value) => dispatch(updateAndPatchUser('status', value))
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(UserStatusSelector));

